import { useState, useCallback } from "react";
import { FilenameConflict } from "../../api/media";

interface FileToUpload {
  file: File;
  originalName: string;
  newName: string;
  skip: boolean;
  conflict: FilenameConflict | null;
}

interface UploadConflictModalProps {
  files: File[];
  conflicts: FilenameConflict[];
  onConfirm: (filesToUpload: Array<{ file: File; filename: string }>) => void;
  onCancel: () => void;
}

export function UploadConflictModal({
  files,
  conflicts,
  onConfirm,
  onCancel,
}: UploadConflictModalProps) {
  // Build initial state mapping files to their conflict status
  const [filesToUpload, setFilesToUpload] = useState<FileToUpload[]>(() => {
    const conflictMap = new Map(conflicts.map((c) => [c.filename, c]));
    return files.map((file) => ({
      file,
      originalName: file.name,
      newName: file.name,
      skip: false,
      conflict: conflictMap.get(file.name) || null,
    }));
  });

  const hasConflicts = conflicts.length > 0;
  const conflictingFiles = filesToUpload.filter((f) => f.conflict && !f.skip);
  const unresolvedConflicts = conflictingFiles.filter(
    (f) => f.newName === f.originalName
  );

  const handleNameChange = useCallback((index: number, newName: string) => {
    setFilesToUpload((prev) =>
      prev.map((f, i) => (i === index ? { ...f, newName } : f))
    );
  }, []);

  const handleSkipToggle = useCallback((index: number) => {
    setFilesToUpload((prev) =>
      prev.map((f, i) => (i === index ? { ...f, skip: !f.skip } : f))
    );
  }, []);

  const handleSkipAllConflicts = useCallback(() => {
    setFilesToUpload((prev) =>
      prev.map((f) => (f.conflict ? { ...f, skip: true } : f))
    );
  }, []);

  const handleAddSuffix = useCallback((index: number) => {
    setFilesToUpload((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        const ext = f.originalName.lastIndexOf(".");
        const name = ext > 0 ? f.originalName.slice(0, ext) : f.originalName;
        const extension = ext > 0 ? f.originalName.slice(ext) : "";
        const timestamp = new Date().toISOString().slice(0, 10);
        return { ...f, newName: `${name}_${timestamp}${extension}` };
      })
    );
  }, []);

  const handleConfirm = useCallback(() => {
    const toUpload = filesToUpload
      .filter((f) => !f.skip)
      .map((f) => ({
        file: f.file,
        filename: f.newName,
      }));
    onConfirm(toUpload);
  }, [filesToUpload, onConfirm]);

  const uploadCount = filesToUpload.filter((f) => !f.skip).length;
  const skipCount = filesToUpload.filter((f) => f.skip).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onCancel}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b bg-vault-50 px-6 py-4">
          <h2 className="text-lg font-semibold text-vault-900">
            {hasConflicts ? "Filename Conflicts Detected" : "Confirm Upload"}
          </h2>
          <p className="mt-1 text-sm text-vault-600">
            {hasConflicts
              ? `${conflicts.length} of ${files.length} files already exist in this vault. Rename or skip them to continue.`
              : `Ready to upload ${files.length} file${files.length !== 1 ? "s" : ""}.`}
          </p>
        </div>

        {/* File List */}
        <div className="max-h-[50vh] overflow-y-auto px-6 py-4">
          <div className="space-y-3">
            {filesToUpload.map((item, index) => (
              <div
                key={`${item.originalName}-${index}`}
                className={`rounded-lg border p-3 ${
                  item.skip
                    ? "border-vault-200 bg-vault-50 opacity-60"
                    : item.conflict
                      ? item.newName === item.originalName
                        ? "border-amber-300 bg-amber-50"
                        : "border-green-300 bg-green-50"
                      : "border-vault-200 bg-white"
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">
                        {item.file.type.startsWith("image/")
                          ? "🖼️"
                          : item.file.type.startsWith("audio/")
                            ? "🎵"
                            : item.file.type.startsWith("video/")
                              ? "🎬"
                              : "📄"}
                      </span>
                      <span
                        className={`text-sm font-medium ${item.skip ? "text-vault-400 line-through" : "text-vault-900"}`}
                      >
                        {item.originalName}
                      </span>
                      <span className="text-xs text-vault-500">
                        ({(item.file.size / 1024).toFixed(0)} KB)
                      </span>
                    </div>

                    {/* Conflict Warning */}
                    {item.conflict && !item.skip && (
                      <div className="mt-2">
                        <p className="text-xs text-amber-700">
                          ⚠️ A file with this name was uploaded on{" "}
                          {new Date(item.conflict.existing_created_at).toLocaleDateString()}
                          {item.conflict.existing_caption && (
                            <> with caption &quot;{item.conflict.existing_caption}&quot;</>
                          )}
                        </p>

                        {/* Rename Input */}
                        <div className="mt-2 flex items-center gap-2">
                          <label className="text-xs font-medium text-vault-600">
                            Rename to:
                          </label>
                          <input
                            type="text"
                            value={item.newName}
                            onChange={(e) => handleNameChange(index, e.target.value)}
                            className={`flex-1 rounded border px-2 py-1 text-sm ${
                              item.newName === item.originalName
                                ? "border-amber-300 bg-white"
                                : "border-green-400 bg-green-50"
                            }`}
                          />
                          <button
                            onClick={() => handleAddSuffix(index)}
                            className="rounded bg-vault-100 px-2 py-1 text-xs text-vault-700 hover:bg-vault-200"
                            title="Add date suffix"
                          >
                            + Date
                          </button>
                        </div>

                        {item.newName !== item.originalName && (
                          <p className="mt-1 text-xs text-green-700">
                            ✓ Will be saved as &quot;{item.newName}&quot;
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Skip Toggle */}
                  <button
                    onClick={() => handleSkipToggle(index)}
                    className={`shrink-0 rounded px-3 py-1 text-xs font-medium transition-colors ${
                      item.skip
                        ? "bg-vault-200 text-vault-600 hover:bg-vault-300"
                        : "bg-vault-100 text-vault-500 hover:bg-red-100 hover:text-red-700"
                    }`}
                  >
                    {item.skip ? "Include" : "Skip"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t bg-vault-50 px-6 py-4">
          <div className="text-sm text-vault-600">
            {uploadCount > 0 && (
              <span className="text-green-700">
                {uploadCount} file{uploadCount !== 1 ? "s" : ""} to upload
              </span>
            )}
            {skipCount > 0 && (
              <span className="ml-2 text-vault-500">
                ({skipCount} skipped)
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {hasConflicts && unresolvedConflicts.length > 0 && (
              <button
                onClick={handleSkipAllConflicts}
                className="rounded border border-vault-300 px-3 py-2 text-sm text-vault-600 hover:bg-vault-100"
              >
                Skip All Conflicts
              </button>
            )}
            <button
              onClick={onCancel}
              className="rounded border border-vault-300 px-4 py-2 text-sm text-vault-600 hover:bg-vault-100"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={uploadCount === 0 || unresolvedConflicts.length > 0}
              className={`rounded px-4 py-2 text-sm font-medium text-white transition-colors ${
                uploadCount === 0 || unresolvedConflicts.length > 0
                  ? "cursor-not-allowed bg-vault-300"
                  : "bg-vault-600 hover:bg-vault-700"
              }`}
            >
              {unresolvedConflicts.length > 0
                ? `Resolve ${unresolvedConflicts.length} Conflict${unresolvedConflicts.length !== 1 ? "s" : ""}`
                : `Upload ${uploadCount} File${uploadCount !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
